'use client'

/**
 * Browser-side API access (T-WEB-08).
 *
 * Goes through the public origin (`/api/*`, which Caddy strips and proxies)
 * so the session cookie is sent as a first-party cookie — the whole point of
 * the single-origin setup in D-02.
 *
 * Every request carries `X-Uniap-Request: 1` (D-09). A browser cannot set a
 * custom header cross-origin without a preflight the API never answers, so
 * its presence is what proves same-origin intent.
 */
import {ApiError, toApiError} from './errors'

export const CSRF_HEADER = 'X-Uniap-Request'

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      [CSRF_HEADER]: '1',
      ...(body === undefined ? {} : {'content-type': 'application/json'})
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  })

  if (!response.ok) throw await toApiError(response)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  get: <T,>(path: string) => request<T>('GET', path),
  post: <T,>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T,>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T,>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  delete: <T,>(path: string) => request<T>('DELETE', path)
}

export {ApiError}
